import { Card as AntCard, CardProps as AntCardProps, theme } from 'antd';
import styled from 'styled-components';
import type { GlobalToken } from 'antd';
import { radius } from '../../theme/radius';

/** 'spacious' = paddingXL vertical, paddingLG horizontal (section cards). */
export type CardPadding = 'none' | 'small' | 'medium' | 'large' | 'spacious';
export type CardShadow = 'none' | 'small' | 'medium' | 'large';

export interface CardProps extends Omit<AntCardProps, 'size'> {
  /** Title of the card */
  title?: React.ReactNode;
  /** Subtitle below the title */
  subtitle?: string;
  /** Extra content in the card header (e.g., actions) */
  extra?: React.ReactNode;
  /** Card content */
  children?: React.ReactNode;
  /** Padding size */
  padding?: CardPadding;
  /** Shadow size */
  shadow?: CardShadow;
  /** Whether to show border */
  bordered?: boolean;
  /** Whether the card is hoverable */
  hoverable?: boolean;
  /** Click handler */
  onClick?: () => void;
}

const getPaddingValues = (
  token: GlobalToken,
  padding: CardPadding,
): { y: number; x: number } => {
  const map: Record<CardPadding, { y: number; x: number }> = {
    none: { y: 0, x: 0 },
    small: { y: token.paddingSM, x: token.paddingSM },
    medium: { y: token.padding, x: token.padding },
    large: { y: token.paddingLG, x: token.paddingLG },
    spacious: { y: token.paddingXL, x: token.paddingLG },
  };
  return map[padding];
};

const paddingY = (token: GlobalToken, padding: CardPadding) =>
  getPaddingValues(token, padding).y;
const paddingX = (token: GlobalToken, padding: CardPadding) =>
  getPaddingValues(token, padding).x;

const getShadowValue = (token: GlobalToken, shadow: CardShadow): string => {
  const map: Record<CardShadow, string> = {
    none: 'none',
    small: token.boxShadowTertiary,
    medium: token.boxShadowSecondary,
    large: token.boxShadow,
  };
  return map[shadow];
};

interface StyledCardProps {
  $token: GlobalToken;
  $padding: CardPadding;
  $shadow: CardShadow;
  $bordered: boolean;
  $hoverable: boolean;
  $clickable: boolean;
}

const StyledCard = styled(AntCard)<StyledCardProps>`
  /* Every card is a box: the shared box radius (the cover follows it). */
  border-radius: ${radius.box}px;
  border: ${({ $bordered, $token }) =>
    $bordered
      ? `${$token.lineWidth}px solid ${$token.colorBorderSecondary}`
      : 'none'};
  box-shadow: ${({ $shadow, $token }) => getShadowValue($token, $shadow)};
  background: ${({ $token }) => $token.colorBgContainer};
  transition: all ${({ $token }) => $token.motionDurationMid} ease;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  ${({ $hoverable, $token }) =>
    $hoverable &&
    `
    &:hover {
      box-shadow: ${$token.boxShadow};
      transform: translateY(-2px);
    }
  `}

  .ant-card-cover {
    overflow: hidden;
    border-radius: ${radius.box}px ${radius.box}px 0 0;
  }

  .ant-card-head {
    padding: 0 ${({ $padding, $token }) => paddingX($token, $padding)}px;
    min-height: auto;
    border-bottom: ${({ $token }) =>
      `${$token.lineWidth}px solid ${$token.colorBorderSecondary}`};
    font-family: ${({ $token }) => $token.fontFamily};
  }

  .ant-card-head-title {
    padding: ${({ $padding, $token }) => paddingY($token, $padding)}px 0;
    font-size: ${({ $token }) => $token.fontSizeLG}px;
    font-weight: ${({ $token }) => $token.fontWeightStrong};
    color: ${({ $token }) => $token.colorText};
  }

  .ant-card-extra {
    padding: ${({ $padding, $token }) => paddingY($token, $padding)}px 0;
  }

  .ant-card-body {
    padding: ${({ $padding, $token }) =>
      `${paddingY($token, $padding)}px ${paddingX($token, $padding)}px`};
  }

  /* When no header, remove extra spacing */
  &.ant-card-no-header {
    .ant-card-body {
      padding: ${({ $padding, $token }) =>
        `${paddingY($token, $padding)}px ${paddingX($token, $padding)}px`};
    }
  }
`;

interface TitleWrapperProps {
  $token: GlobalToken;
}

const TitleWrapper = styled.div<TitleWrapperProps>`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Subtitle = styled.span<TitleWrapperProps>`
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  font-weight: 400;
  color: ${({ $token }) => $token.colorTextTertiary};
`;

/**
 * Card component based on Libertify Design System
 *
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { Card } from '@libertify/ui';
 *
 * // Basic card
 * <Card>
 *   <p>Card content</p>
 * </Card>
 *
 * // Card with header
 * <Card title="Card Title" subtitle="Card subtitle" extra={<Button>Action</Button>}>
 *   <p>Card content</p>
 * </Card>
 *
 * // Hoverable card
 * <Card hoverable onClick={() => console.log('clicked')}>
 *   <p>Click me</p>
 * </Card>
 * ```
 */
export const Card = ({
  title,
  subtitle,
  extra,
  children,
  padding = 'medium',
  shadow = 'small',
  bordered = true,
  hoverable = false,
  onClick,
  ...props
}: CardProps) => {
  const { token } = theme.useToken();

  // Build title with subtitle if provided
  const cardTitle =
    title && subtitle ? (
      <TitleWrapper $token={token}>
        <span>{title}</span>
        <Subtitle $token={token}>{subtitle}</Subtitle>
      </TitleWrapper>
    ) : (
      title
    );

  return (
    <StyledCard
      $token={token}
      $padding={padding}
      $shadow={shadow}
      $bordered={bordered}
      $hoverable={hoverable}
      $clickable={!!onClick}
      title={cardTitle}
      extra={extra}
      onClick={onClick}
      {...props}
    >
      {children}
    </StyledCard>
  );
};

export default Card;
